import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";

const SEVERITIES = ["low", "medium", "high", "urgent"];

export default function IssueDialog({ propertyId, jobId, onCreated, showPropertyPicker }) {
  const [open, setOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({ property_id: propertyId || "", title: "", description: "", severity: "low" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (showPropertyPicker) api.get("/properties").then(({ data }) => setProperties(data));
  }, [showPropertyPicker]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/issues", { ...form, job_id: jobId || null, photos: [] });
      toast.success("Issue reported");
      setForm({ property_id: propertyId || "", title: "", description: "", severity: "low" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not report issue");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="report-issue-btn" variant="outline" className="border-gold text-royal-deep hover:bg-gold/10 gap-2">
          <AlertTriangle className="h-4 w-4 text-gold" /> Report issue
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-royal-deep">Report an issue</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="issue-form">
          {showPropertyPicker && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Property</Label>
              <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
                <SelectTrigger data-testid="issue-property-select" className="mt-1.5"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p._id} value={p._id} data-testid={`issue-property-option-${p._id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title</Label>
            <Input data-testid="issue-title-input" required value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">What did you find?</Label>
            <Textarea data-testid="issue-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Severity</Label>
            <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
              <SelectTrigger data-testid="issue-severity-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`severity-option-${s}`}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="submit-issue-btn" type="submit" disabled={busy || !form.property_id} className="w-full bg-royal hover:bg-royal-deep text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit issue"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
